import { useState, useEffect, useMemo } from 'react';
import { Formik, FieldArray, Form } from 'formik';
import * as Yup from 'yup';
import {
  Box, Button, Grid, TextField, Typography, Switch, FormControlLabel,
  Paper, Chip, Stack, MenuItem, FormControl, InputLabel, Select,
  IconButton, Accordion, AccordionSummary, AccordionDetails,
  Tabs, Tab, Divider, InputAdornment, Alert, Avatar, Badge,
} from '@mui/material';
import {
  Delete, Add, ExpandMore, Info, Route, AttachMoney, LocalOffer,
  CheckCircle, Backpack, Article, Map, LinkIcon, Schedule, Height,
  StraightenOutlined, AccessTime, DirectionsCar, Description,
  Image as ImageIcon, KeyboardArrowRight, ArrowBack,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { createPackage, updatePackage } from '../../api/packageAPI';
import { getAllLocations } from '../../api/locationAPI';
import useSnackbar from '../../hooks/useSnackbar';
import { useSelector, useDispatch } from 'react-redux';
import { setLastEditedPackageId } from '../../reduxcomponents/slices/packagesSlice';
import _ from 'lodash';
import PackageAiUpload from './PackageAiUpload';

const REGIONS = [
  { label: 'None', val: '' },
  { label: 'Sikkim', val: 'Sikkim' },
  { label: 'Darjeeling', val: 'Darjeeling' },
  { label: 'North Sikkim', val: 'North Sikkim' },
  { label: 'East Sikkim', val: 'East Sikkim' },
  { label: 'West Sikkim', val: 'West Sikkim' },
  { label: 'South Sikkim', val: 'South Sikkim' },
  { label: 'Meghalaya', val: 'Meghalaya' },
  { label: 'Arunachal Pradesh', val: 'Arunachal Pradesh' },
  { label: 'Kerala', val: 'Kerala' },
  { label: 'Dooars', val: 'Dooars' },
  { label: 'Ladakh', val: 'Ladakh' },
  { label: 'Bhutan', val: 'Bhutan' },
  { label: 'Darjeeling - Sikkim', val: 'Darjeeling - Sikkim' },
];

const normalizeRegion = (rawLoc, availableLocations = []) => {
  if (!rawLoc) return '';
  const trimmed = String(rawLoc).trim();
  const lower = trimmed.toLowerCase();

  // Match against DB locations (e.g. 'Sikkim', 'Darjeeling', 'North Sikkim', 'Meghalaya', etc.)
  if (Array.isArray(availableLocations) && availableLocations.length > 0) {
    const exact = availableLocations.find(l => (l?.name || '').toLowerCase() === lower);
    if (exact) return exact.name;

    const partial = availableLocations.find(l => {
      const n = (l?.name || '').toLowerCase();
      return lower.includes(n) || n.includes(lower);
    });
    if (partial) return partial.name;
  }

  // Match against static REGIONS fallback
  const staticMatch = REGIONS.find(r => r.val && (r.val.toLowerCase() === lower || r.label.toLowerCase() === lower));
  if (staticMatch) {
    if (Array.isArray(availableLocations) && availableLocations.length > 0) {
      const dbMatch = availableLocations.find(l => (l?.name || '').toLowerCase() === staticMatch.label.toLowerCase());
      if (dbMatch) return dbMatch.name;
    }
    return staticMatch.val || staticMatch.label;
  }

  return trimmed.replace(/\b\w/g, c => c.toUpperCase());
};

// ─── Initial values ───────────────────────────────────────────────────────────
const getInitialValues = (data, availableLocations = []) => {
  const base = {
    disabled: false, image: '', duration: '', isActive: false, tags: [],
    location: '', type: '', url: '', label: '',
    meta: { title: '', description: '', keywords: [] },
    shortInclusions: [''],
    details: {
      header: { h1: '', h2: '', h3: '' },
      overview: [{ tagName: '', tagValue: '' }],
      covering: [''],
      shortItinerary: [{ tagName: '', tagValue: '' }],
      itinerary: [{ headerTag: '', headerValue: '', Distance: '', Duration: '', Altitude: '', Time: '', para: [''], images: [''], activity: [''], inclusions: [''] }],
      howToReach: { tldr: '', note: [''], para: [''], itineraryReach: [{ tagName: '', tagValue: [''] }] },
      cost: {
        singleCost: '',
        multipleCost: [{ pax: '', pricing: [{ catagory: '', price: '' }] }],
        daysCost: [{ days: '', pricing: [{ catagory: '', price: '' }] }],
        valueCost: [{ type: '', price: '' }],
        inclusions: [''], exclusions: [''],
      },
      thingsToCarry: { Basics: [], Documents: [], Clothing: [], Medicine: [], Toiletries: [], Accessories: [] },
    },
    packageAtAGlance: { TLDR: '', notes: [''], description: [{ title: '', details: [''] }] },
    quickFactCheck: { TLDR: '', notes: [''], description: [{ title: '', details: [''] }] },
    bestSuitedFor: { TLDR: '', notes: [''], description: [{ title: '', details: [''] }] },
    bestTimeToVisit: { TLDR: '', notes: [''], description: [{ title: '', details: [''] }] },
    packageIncludes: { TLDR: '', notes: [''], description: [{ title: '', details: [''] }] },
    packageExcludes: { TLDR: '', notes: [''], description: [{ title: '', details: [''] }] },
    whyTravelWithUs: { TLDR: '', notes: [''], description: [{ title: '', details: [''] }] },
    faqs: [{ question: '', answer: '' }],
  };

  if (!data) return base;

  const normalizeSection = (sec) => {
    if (!sec) {
      return { TLDR: '', notes: [''], description: [{ title: '', details: [''] }] };
    }
    const TLDR = sec.TLDR !== undefined ? sec.TLDR : (sec.tldr || '');
    const rawNotes = sec.notes !== undefined ? sec.notes : (sec.note || []);
    const notes = Array.isArray(rawNotes) && rawNotes.length > 0 ? rawNotes.filter(Boolean) : [''];

    let description = [{ title: '', details: [''] }];
    if (Array.isArray(sec.description) && sec.description.length > 0) {
      if (typeof sec.description[0] === 'object' && sec.description[0] !== null && ('details' in sec.description[0] || 'title' in sec.description[0])) {
        description = sec.description.map(d => ({
          title: d.title || 'Highlight',
          details: Array.isArray(d.details) && d.details.length > 0 ? d.details : (d.detail ? [String(d.detail)] : [''])
        }));
      } else {
        description = [{
          title: 'Details',
          details: sec.description.filter(Boolean)
        }];
      }
    }
    return { TLDR, notes: notes.length > 0 ? notes : [''], description: description.length > 0 ? description : [{ title: '', details: [''] }] };
  };

  const merged = _.merge({}, base, data);

  // Normalize Region
  merged.location = normalizeRegion(data.location || merged.location, availableLocations);

  // Calculate synchronized duration, shortItinerary, and itinerary
  const rawFullDays = Array.isArray(data?.details?.itinerary) && data.details.itinerary.length > 0
    ? data.details.itinerary
    : (Array.isArray(data?.itinerary) && data.itinerary.length > 0 ? data.itinerary : []);

  const rawShortDays = Array.isArray(data?.details?.shortItinerary) && data.details.shortItinerary.length > 0
    ? data.details.shortItinerary
    : (Array.isArray(data?.shortItinerary) && data.shortItinerary.length > 0 ? data.shortItinerary : []);

  const totalDays = Math.max(
    parseInt(data.duration) || 0,
    parseInt(merged.duration) || 0,
    rawFullDays.length,
    rawShortDays.length,
    1
  );
  merged.duration = totalDays;

  const cleanDayStr = (str = '', fallback = '') => {
    if (!str) return fallback;
    return String(str)
      .replace(/^day\s*\d+\s*[:\-–]\s*/i, '')
      .replace(/^day\s*\d+\s*/i, '')
      .trim() || fallback;
  };

  const syncShort = [];
  const syncFull = [];

  for (let i = 0; i < totalDays; i++) {
    const dayLabel = `Day ${i + 1}`;
    const f = rawFullDays[i] || {};
    const s = rawShortDays[i] || {};

    const rawT = s.tagValue || f.headerValue || f.title || f.headerTag || '';
    const dayTitle = cleanDayStr(rawT, `Day ${i + 1} Sightseeing & Transfer`);

    syncShort.push({
      tagName: dayLabel,
      tagValue: dayTitle,
    });

    syncFull.push({
      headerTag: dayLabel,
      headerValue: dayTitle,
      Distance: f.Distance || f.distance || '',
      Duration: f.Duration || f.duration || '',
      Altitude: f.Altitude || f.altitude || '',
      Time: f.Time || f.time || '',
      para: Array.isArray(f.para) && f.para.length > 0 ? f.para : (f.description ? [String(f.description)] : [dayTitle]),
      images: Array.isArray(f.images) && f.images.length > 0 ? f.images : [''],
      activity: Array.isArray(f.activity) ? f.activity : (Array.isArray(f.activities) ? f.activities : []),
      inclusions: Array.isArray(f.inclusions) ? f.inclusions : [],
    });
  }

  merged.details.shortItinerary = syncShort;
  merged.details.itinerary = syncFull;

  // Overview
  if (Array.isArray(data?.details?.overview) && data.details.overview.length > 0) {
    merged.details.overview = data.details.overview.filter(o => o && (o.tagName || o.tagValue));
  } else if (!merged.details.overview || merged.details.overview.length === 0 || !merged.details.overview[0]?.tagName) {
    merged.details.overview = [
      { tagName: 'Destination', tagValue: merged.location || 'Himalayas' },
      { tagName: 'Duration', tagValue: `${totalDays} Days` },
      { tagName: 'Start Point', tagValue: 'Nearest Airport / Station' },
      { tagName: 'End Point', tagValue: 'Nearest Airport / Station' }
    ];
  }

  // Covering & Tags
  if (Array.isArray(data?.details?.covering) && data.details.covering.length > 0) {
    merged.details.covering = data.details.covering.filter(Boolean);
  }
  if (Array.isArray(data?.tags) && data.tags.length > 0) {
    merged.tags = data.tags.filter(Boolean);
  }
  if (Array.isArray(data?.shortInclusions) && data.shortInclusions.length > 0) {
    merged.shortInclusions = data.shortInclusions.filter(Boolean);
  }

  // Costing
  const rawMulti = data?.details?.cost?.multipleCost || data?.multipleCost;
  if (Array.isArray(rawMulti) && rawMulti.length > 0) {
    merged.details.cost.multipleCost = rawMulti.map((mc, idx) => ({
      pax: mc.pax !== undefined && mc.pax !== '' ? Number(mc.pax) : (idx + 1) * 2,
      pricing: Array.isArray(mc.pricing) && mc.pricing.length > 0
        ? mc.pricing.map(p => ({
            catagory: p.catagory || p.category || 'Standard',
            price: p.price !== undefined && p.price !== '' ? Number(p.price) : ''
          }))
        : [{ catagory: 'Standard', price: '' }]
    }));
  } else {
    const baseP = Number(data?.details?.cost?.singleCost || data?.singleCost) || 18500;
    merged.details.cost.singleCost = baseP;
    merged.details.cost.multipleCost = [
      {
        pax: 2,
        pricing: [
          { catagory: 'Standard', price: baseP },
          { catagory: 'Deluxe', price: Math.round(baseP * 1.3) }
        ]
      },
      {
        pax: 4,
        pricing: [
          { catagory: 'Standard', price: Math.round(baseP * 0.75) },
          { catagory: 'Deluxe', price: Math.round(baseP * 1.05) }
        ]
      }
    ];
  }

  // Inclusions & Exclusions
  const incs = [
    ...(Array.isArray(data?.details?.cost?.inclusions) ? data.details.cost.inclusions : []),
    ...(Array.isArray(data?.inclusions) ? data.inclusions : []),
    ...(Array.isArray(data?.details?.inclusions) ? data.details.inclusions : [])
  ].filter(Boolean);
  merged.details.cost.inclusions = incs.length > 0 ? incs : [
    'Accommodation in selected hotel category',
    'Daily breakfast and dinner (MAP plan)',
    'Exclusive private vehicle for transfers and sightseeing',
    'All inner line permits, driver allowances, toll taxes, and parking fees'
  ];

  const excs = [
    ...(Array.isArray(data?.details?.cost?.exclusions) ? data.details.cost.exclusions : []),
    ...(Array.isArray(data?.exclusions) ? data.exclusions : []),
    ...(Array.isArray(data?.details?.exclusions) ? data.details.exclusions : [])
  ].filter(Boolean);
  merged.details.cost.exclusions = excs.length > 0 ? excs : [
    'Airfare / Train tickets',
    'Entry tickets to monasteries, ropeways, monuments, or viewpoints',
    'Personal expenses, laundry, tips, room heater, and optional activities'
  ];

  // How to Reach
  if (data?.details?.howToReach) {
    const r = data.details.howToReach;
    merged.details.howToReach = {
      tldr: r.tldr || '',
      note: Array.isArray(r.note) && r.note.length > 0 ? r.note : [''],
      para: Array.isArray(r.para) && r.para.length > 0 ? r.para : [''],
      itineraryReach: Array.isArray(r.itineraryReach) && r.itineraryReach.length > 0
        ? r.itineraryReach.map(it => ({
            tagName: it.tagName || 'Route',
            tagValue: Array.isArray(it.tagValue) ? it.tagValue : (it.tagValue ? [String(it.tagValue)] : [''])
          }))
        : [{ tagName: 'By Air / Rail', tagValue: ['Nearest Airport / Station'] }]
    };
  }

  // Things to Carry
  const carryCategories = ['Basics', 'Documents', 'Clothing', 'Medicine', 'Toiletries', 'Accessories'];
  const rawCarry = data?.details?.thingsToCarry || data?.thingsToCarry || {};
  carryCategories.forEach(cat => {
    let list = [];
    for (const k of Object.keys(rawCarry)) {
      if (k.toLowerCase() === cat.toLowerCase() || k.toLowerCase().startsWith(cat.toLowerCase().slice(0, 4))) {
        if (Array.isArray(rawCarry[k]) && rawCarry[k].length > 0) {
          list = rawCarry[k].filter(Boolean);
          break;
        }
      }
    }
    if (list.length === 0) {
      if (cat === 'Basics') list = ['Govt Photo ID (Voter ID/Passport/Aadhaar)', 'Passport size photos (4 copies)', 'Adequate cash for remote areas'];
      else if (cat === 'Documents') list = ['Original Photo ID & photocopies', 'Inner line & travel permit copies', 'Hotel booking vouchers'];
      else if (cat === 'Clothing') list = ['Warm woolen clothes and thermals', 'Comfortable trekking/walking shoes with grip', 'Raincoat or umbrella'];
      else if (cat === 'Medicine') list = ['Altitude sickness medication (Diamox)', 'Motion sickness pills', 'Personal prescription medicines', 'First aid kit'];
      else if (cat === 'Toiletries') list = ['Sunscreen lotion SPF 50+', 'Lip balm & cold moisturizer', 'Hand sanitizer & disinfectant wipes'];
      else if (cat === 'Accessories') list = ['High-capacity power bank', 'UV protection sunglasses', 'Thermos water bottle', 'Camera with extra memory card'];
    }
    merged.details.thingsToCarry[cat] = list;
  });

  // FAQs
  if (Array.isArray(data?.faqs) && data.faqs.length > 0) {
    merged.faqs = data.faqs.filter(f => f && (f.question || f.answer));
  } else if (!merged.faqs || merged.faqs.length === 0) {
    merged.faqs = [
      { question: `What is the best time to visit ${merged.location || 'this destination'}?`, answer: 'Spring (March to June) and Autumn (October to December) offer the best mountain visibility and pleasant temperatures.' },
      { question: 'Is this itinerary suitable for families and senior citizens?', answer: 'Yes, this package is designed with comfortable pacing, quality hotels, and dedicated private vehicle transfers.' },
      { question: 'Who arranges the required permits?', answer: 'Our operations team handles all required inner-line checkpost permits in advance using your ID copies.' }
    ];
  }

  // Marketing sections
  const marketingKeys = [
    'packageAtAGlance',
    'quickFactCheck',
    'bestSuitedFor',
    'bestTimeToVisit',
    'packageIncludes',
    'packageExcludes',
    'whyTravelWithUs'
  ];
  marketingKeys.forEach(key => {
    merged[key] = normalizeSection(data?.[key] || merged[key]);
  });

  return merged;
};

const validationSchema = Yup.object({
  location: Yup.string().required('Location is required'),
  type: Yup.string().required('Type is required'),
  url: Yup.string().required('URL is required'),
  label: Yup.string().required('Label is required'),
});

const CARRY_TABS = ['Basics', 'Documents', 'Clothing', 'Medicine', 'Toiletries', 'Accessories'];

// ─── Sidebar sections ────────────────────────────────────────────────────────
const SECTIONS = [
  { key: 'basic', label: 'Basic Info', emoji: '📦', color: '#667eea' },
  { key: 'meta', label: 'SEO & Meta', emoji: '🔍', color: '#a855f7' },
  { key: 'header', label: 'Page Header', emoji: '🏷️', color: '#22c55e' },
  { key: 'tags', label: 'Tags & Covering', emoji: '📍', color: '#f97316' },
  { key: 'overview', label: 'Overview', emoji: '📋', color: '#f59e0b' },
  { key: 'shortItinerary', label: 'Short Itinerary', emoji: '🗓️', color: '#0ea5e9' },
  { key: 'itinerary', label: 'Full Itinerary', emoji: '🗺️', color: '#06b6d4' },
  { key: 'reach', label: 'How To Reach', emoji: '🚗', color: '#d946ef' },
  { key: 'cost', label: 'Pricing & Costs', emoji: '💰', color: '#10b981' },
  { key: 'carry', label: 'Things To Carry', emoji: '🎒', color: '#f43f5e' },
  { key: 'marketing', label: 'Marketing Info', emoji: '📢', color: '#fbbf24' },
  { key: 'faqs', label: 'FAQs', emoji: '❓', color: '#f59e0b' },
];

// ─── Chip list editor ────────────────────────────────────────────────────────
const ChipListEditor = ({ name, values = [], setFieldValue, label, placeholder }) => {
  const [input, setInput] = useState('');
  const [editIdx, setEditIdx] = useState(null);
  const commit = () => {
    if (!input.trim()) return;
    const next = [...values];
    if (editIdx !== null) { next[editIdx] = input; setEditIdx(null); } else next.push(input);
    setFieldValue(name, next);
    setInput('');
  };
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
        <TextField size="small" fullWidth placeholder={placeholder || `Add ${label}…`} value={input}
          onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), commit())} />
        <Button variant="contained" onClick={commit} sx={{ minWidth: 72, borderRadius: 2, flexShrink: 0 }}>
          {editIdx !== null ? 'Update' : 'Add'}
        </Button>
      </Box>
      <Stack direction="row" flexWrap="wrap" gap={0.5}>
        {values.map((item, i) => (
          <Chip key={i} label={item} size="small"
            onClick={() => { setInput(item); setEditIdx(i); }}
            onDelete={() => { const n = [...values]; n.splice(i, 1); setFieldValue(name, n); }}
            deleteIcon={<Delete fontSize="small" />}
            sx={{ bgcolor: '#f1f5f9', fontWeight: 500 }} />
        ))}
      </Stack>
    </Box>
  );
};

// ─── String array rows ───────────────────────────────────────────────────────
const StringArrayEditor = ({ name, values = [], handleChange, setFieldValue, label, multiline = false }) => (
  <FieldArray name={name}>
    {({ push, remove }) => (
      <Box>
        {values.map((val, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
            <TextField label={`${label} ${i + 1}`} name={`${name}[${i}]`} value={val} onChange={handleChange}
              fullWidth size="small" multiline={multiline} minRows={multiline ? 3 : 1} />
            <IconButton onClick={() => remove(i)} color="error" size="small" sx={{ mt: 0.5 }}>
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        ))}
        <Button startIcon={<Add />} size="small" onClick={() => push('')} sx={{ borderRadius: 2, mt: 0.5 }}>
          Add {label}
        </Button>
      </Box>
    )}
  </FieldArray>
);

const FieldLabel = ({ label }) => (
  <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', display: 'block', mb: 0.8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
    {label}
  </Typography>
);

const SectionPanel = ({ activeSection, sectionKey, children }) =>
  activeSection === sectionKey ? <Box>{children}</Box> : null;

// ─── Marketing Field Editor ──────────────────────────────────────────────────
const MarketingFieldEditor = ({ name, values = {}, handleChange, setFieldValue, label, emoji }) => (
  <Box sx={{ mb: 4, p: 2.5, bgcolor: '#fff', borderRadius: 2, border: '1px solid #fde68a', boxShadow: '0 2px 8px rgba(251, 191, 36, 0.08)' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
      <Typography sx={{ fontSize: 24 }}>{emoji}</Typography>
      <Typography variant="h6" sx={{ color: '#b45309', fontWeight: 700 }}>{label}</Typography>
    </Box>
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          label="TLDR"
          name={`${name}.TLDR`}
          value={_.get(values, 'TLDR', '')}
          onChange={handleChange}
          fullWidth
          size="small"
        />
      </Grid>
      <Grid item xs={12}>
        <FieldLabel label="Notes" />
        <StringArrayEditor
          name={`${name}.notes`}
          values={_.get(values, 'notes', [])}
          handleChange={handleChange}
          setFieldValue={setFieldValue}
          label="Note"
          multiline
        />
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ my: 1.5 }} />
        <FieldLabel label="Description Blocks" />
        <FieldArray name={`${name}.description`}>
          {({ push, remove }) => (
            <Grid container spacing={2}>
              {(_.get(values, 'description', []) || []).map((desc, index) => (
                <Grid item xs={12} key={index} sx={{ mb: 2, p: 2, border: '1px dashed #cbd5e1', borderRadius: 2, bgcolor: '#f8fafc' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569' }}>
                      Description Block {index + 1}
                    </Typography>
                    <Button onClick={() => remove(index)} color="error" size="small">
                      Remove Block
                    </Button>
                  </Box>
                  <TextField
                    label="Description Title"
                    name={`${name}.description[${index}].title`}
                    value={desc.title || ''}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    sx={{ mb: 2 }}
                  />
                  <FieldLabel label="Description Details (Paragraphs)" />
                  <StringArrayEditor
                    name={`${name}.description[${index}].details`}
                    values={desc.details || []}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                    label="Description Paragraph"
                    multiline
                  />
                </Grid>
              ))}
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => push({ title: '', details: [''] })}
                  sx={{ borderRadius: 2, color: '#b45309', borderColor: '#fde68a', '&:hover': { borderColor: '#f59e0b', bgcolor: '#fffbeb' } }}
                >
                  Add Description Block
                </Button>
              </Grid>
            </Grid>
          )}
        </FieldArray>
      </Grid>
    </Grid>
  </Box>
);

// ─── Main Form ────────────────────────────────────────────────────────────────
const PackageForm = () => {
  const { fetchSelectedPackage: sel } = useSelector(s => s.package);
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('basic');
  const [carryTab, setCarryTab] = useState(0);
  const [costTab, setCostTab] = useState(0);
  const [locations, setLocations] = useState([]);
  const [aiData, setAiData] = useState(null);
  const isEdit = !!(sel && sel._id);

  useEffect(() => {
    getAllLocations({ isActive: true })
      .then(res => {
        if (Array.isArray(res?.data)) {
          setLocations(res.data);
        }
      })
      .catch(() => { });
  }, []);

  const formInitialValues = useMemo(() => {
    return getInitialValues(aiData || sel, locations);
  }, [aiData, sel, locations]);

  const handleSubmit = async (values) => {
    try {
      if (isEdit) {
        await updatePackage(values, sel._id);
        dispatch(setLastEditedPackageId(sel._id));
        showSnackbar('Package updated successfully', 'success');
      } else {
        const res = await createPackage(values);
        const newId = res?.data?._id || res?._id;
        if (newId) dispatch(setLastEditedPackageId(newId));
        showSnackbar('Package created successfully', 'success');
      }
    } catch { showSnackbar('Something went wrong', 'error'); }
  };

  return (
    <>
      <Formik
        initialValues={formInitialValues}
        enableReinitialize
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ values, handleChange, setFieldValue, setValues, errors, touched }) => (
          <Box sx={{ bgcolor: '#f8fafc', minHeight: '100%' }}>

            {/* ── Top bar ─────────────────────────────────────────────────── */}
            <Box sx={{
              position: 'sticky', top: 64, zIndex: 1000,
              background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
              px: 3, py: 1.5, display: 'flex', alignItems: 'center', gap: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            }}>
              <IconButton
                onClick={() => navigate(-1)}
                size="small"
                sx={{
                  color: '#fff', bgcolor: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                  flexShrink: 0,
                }}
              >
                <ArrowBack fontSize="small" />
              </IconButton>

              <Box sx={{ flexShrink: 0 }}>
                <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 800, lineHeight: 1.2 }}>
                  {isEdit ? '✏️ Edit Package' : '📦 Create New Package'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                  {isEdit ? `ID: ${sel._id}` : 'Fill required fields then hit Create'}
                </Typography>
              </Box>

              {/* ── Live info chips ─────────────────────────────── */}
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {[
                  { icon: '🏷️', value: values.label, placeholder: 'No Label' },
                  { icon: '📍', value: values.location, placeholder: 'No Region' },
                  { icon: '🗂️', value: values.type, placeholder: 'No Type' },
                  { icon: '📅', value: values.duration ? `${values.duration}D` : null, placeholder: 'No Duration' },
                ].map(({ icon, value, placeholder }) => (
                  <Chip
                    key={placeholder}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                        <span style={{ fontSize: 12 }}>{icon}</span>
                        <span style={{ fontSize: 11, fontWeight: value ? 600 : 400, opacity: value ? 1 : 0.45 }}>
                          {value || placeholder}
                        </span>
                      </Box>
                    }
                    size="small"
                    sx={{
                      bgcolor: value ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.05)',
                      border: `1px solid ${value ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.08)'}`,
                      color: '#fff',
                      height: 26,
                    }}
                  />
                ))}
              </Stack>

              <FormControlLabel
                control={<Switch name="isActive" checked={values.isActive} onChange={handleChange} size="small" color="success" />}
                label={
                  <Chip
                    label={values.isActive ? 'Active' : 'Inactive'} size="small"
                    sx={{
                      bgcolor: values.isActive ? '#22c55e22' : '#ef444422',
                      color: values.isActive ? '#4ade80' : '#f87171',
                      border: `1px solid ${values.isActive ? '#22c55e55' : '#ef444455'}`,
                      fontWeight: 700, fontSize: 11,
                    }}
                  />
                }
                sx={{ m: 0, gap: 1 }}
              />

              <Button
                variant="contained" type="submit" form="pkg-form"
                sx={{
                  borderRadius: 2, fontWeight: 700, px: 3,
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  boxShadow: '0 4px 15px rgba(102,126,234,0.45)',
                }}
              >
                {isEdit ? 'Update Package' : 'Create Package'}
              </Button>
            </Box>

            {/* ── Body: sidebar + content ──────────────────────────────────── */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>

              {/* ── Left sidebar ─────────────────────────────────────────── */}
              <Box sx={{
                width: 220, flexShrink: 0,
                bgcolor: '#fff',
                borderRight: '1px solid #e2e8f0',
                display: 'flex', flexDirection: 'column',
                position: 'sticky', top: 120,
                height: 'calc(100vh - 120px)',
                overflowY: 'auto',
                py: 1.5,
              }}>
                <Typography variant="caption" sx={{ px: 2, mb: 1, color: '#94a3b8', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
                  Sections
                </Typography>
                {SECTIONS.map(sec => {
                  const active = activeSection === sec.key;
                  return (
                    <Box
                      key={sec.key}
                      onClick={() => setActiveSection(sec.key)}
                      sx={{
                        px: 2, py: 1.2, mx: 1, mb: 0.5, borderRadius: 2,
                        display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer',
                        bgcolor: active ? `${sec.color}15` : 'transparent',
                        borderLeft: active ? `3px solid ${sec.color}` : '3px solid transparent',
                        transition: 'all 0.15s',
                        '&:hover': { bgcolor: `${sec.color}10` },
                      }}
                    >
                      <Typography sx={{ fontSize: 16, lineHeight: 1 }}>{sec.emoji}</Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: active ? 700 : 500, color: active ? sec.color : '#475569', fontSize: 13 }}
                      >
                        {sec.label}
                      </Typography>
                      {active && <KeyboardArrowRight sx={{ ml: 'auto', fontSize: 16, color: sec.color }} />}
                    </Box>
                  );
                })}
              </Box>

              {/* ── Right content ─────────────────────────────────────────── */}
              <Box sx={{ flex: 1, p: 3, minWidth: 0 }}>
                {/* ── AI Auto-Fill Document Card ─────────────────────────── */}
                <PackageAiUpload
                  locations={locations}
                  onExtracted={(extractedPkg) => {
                    const normalized = getInitialValues(extractedPkg, locations);
                    setAiData(normalized);
                    setValues(normalized);
                    showSnackbar(`✨ "${normalized.label || 'Package'}" auto-filled across all form sections!`, 'success');
                  }}
                />

                <Form id="pkg-form">

                  {/* ── Basic Info ──────────────────────────────────────── */}
                  <SectionPanel activeSection={activeSection} sectionKey="basic">
                    <SectionTitle emoji="📦" title="Basic Info" subtitle="Core package details visible on listings" color="#667eea" />
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={12} md={8}>
                        <TextField
                          label="Image URL" name="image" value={values.image} onChange={handleChange} fullWidth size="small"
                          placeholder="https://example.com/cover.jpg"
                          InputProps={{ startAdornment: <InputAdornment position="start">🖼</InputAdornment> }}
                        />
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <TextField
                          label="Duration (days)" name="duration" value={values.duration}
                          onChange={(e) => {
                            handleChange(e);
                            const n = parseInt(e.target.value) || 0;
                            const current = values.details.shortItinerary;
                            if (n > current.length) {
                              const extra = Array.from({ length: n - current.length }, (_, k) => ({
                                tagName: `Day ${current.length + k + 1}`,
                                tagValue: '',
                              }));
                              setFieldValue('details.shortItinerary', [...current, ...extra]);
                            } else {
                              setFieldValue('details.shortItinerary', current.slice(0, n));
                            }
                          }}
                          fullWidth size="small" type="number"
                          InputProps={{ startAdornment: <InputAdornment position="start">📅</InputAdornment> }}
                        />
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Region</InputLabel>
                          <Select value={values.location} label="Region" name="location" onChange={handleChange}
                            error={touched.location && Boolean(errors.location)}>
                            <MenuItem value=""><em>None</em></MenuItem>
                            {(locations.length > 0 ? locations.map(l => ({ val: l.name, label: l.name })) : REGIONS.filter(r => r.val)).map(r => (
                              <MenuItem key={r.val} value={r.val}>{r.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {touched.location && errors.location && <Typography variant="caption" color="error">{errors.location}</Typography>}
                      </Grid>

                      <Grid item xs={6} md={3}>
                        <TextField label="Type" name="type" value={values.type} onChange={handleChange} fullWidth size="small"
                          error={touched.type && Boolean(errors.type)} helperText={touched.type && errors.type}
                          placeholder="e.g. Adventure, Luxury" />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <TextField label="Label" name="label" value={values.label} onChange={handleChange} fullWidth size="small"
                          error={touched.label && Boolean(errors.label)} helperText={touched.label && errors.label}
                          placeholder="e.g. Best Seller, New" />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField label="URL Slug" name="url" value={values.url} onChange={handleChange} fullWidth size="small"
                          error={touched.url && Boolean(errors.url)} helperText={touched.url && errors.url}
                          placeholder="e.g. sikkim-7-days-adventure"
                          InputProps={{ startAdornment: <InputAdornment position="start">🔗</InputAdornment> }} />
                      </Grid>
                    </Grid>
                  </SectionPanel>

                  {/* ── SEO Meta ────────────────────────────────────────── */}
                  <SectionPanel activeSection={activeSection} sectionKey="meta">
                    <SectionTitle emoji="🔍" title="SEO & Meta Data" subtitle="Controls how this package appears in search engines" color="#a855f7" />
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={12}><TextField label="Meta Title" name="meta.title" value={values.meta.title} onChange={handleChange} fullWidth size="small" /></Grid>
                      <Grid item xs={12}><TextField label="Meta Description" name="meta.description" value={values.meta.description} onChange={handleChange} fullWidth size="small" multiline minRows={2} /></Grid>
                      <Grid item xs={12}>
                        <FieldLabel label="Meta Keywords" />
                        <ChipListEditor name="meta.keywords" values={values.meta.keywords} setFieldValue={setFieldValue} label="keyword" placeholder="Type keyword and press Enter…" />
                      </Grid>
                    </Grid>
                  </SectionPanel>

                  {/* ── Page Header ─────────────────────────────────────── */}
                  <SectionPanel activeSection={activeSection} sectionKey="header">
                    <SectionTitle emoji="🏷️" title="Page Header" subtitle="Heading text shown at the top of the package page" color="#22c55e" />
                    <Alert severity="info" sx={{ mt: 1.5, mb: 2, borderRadius: 2 }}>H1 is the main title, H2 is the sub-title, H3 is the supporting tagline.</Alert>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}><TextField label="H1 — Main Heading" name="details.header.h1" value={values.details.header.h1} onChange={handleChange} fullWidth size="small" /></Grid>
                      <Grid item xs={12} md={4}><TextField label="H2 — Sub Heading" name="details.header.h2" value={values.details.header.h2} onChange={handleChange} fullWidth size="small" /></Grid>
                      <Grid item xs={12} md={4}><TextField label="H3 — Tagline" name="details.header.h3" value={values.details.header.h3} onChange={handleChange} fullWidth size="small" /></Grid>
                    </Grid>
                  </SectionPanel>

                  {/* ── Tags & Covering ─────────────────────────────────── */}
                  <SectionPanel activeSection={activeSection} sectionKey="tags">
                    <SectionTitle emoji="📍" title="Tags, Covering Places & Short Inclusions" subtitle="Categorization and quick-view details" color="#f97316" />
                    <Grid container spacing={3} sx={{ mt: 1 }}>
                      <Grid item xs={12} md={4}>
                        <FieldLabel label="🏷 Tags" />
                        <ChipListEditor name="tags" values={values.tags} setFieldValue={setFieldValue} label="tag" />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FieldLabel label="📍 Covering Places" />
                        <ChipListEditor name="details.covering" values={values.details.covering} setFieldValue={setFieldValue} label="place" />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FieldLabel label="✅ Short Inclusions" />
                        <ChipListEditor name="shortInclusions" values={values.shortInclusions} setFieldValue={setFieldValue} label="inclusion" />
                      </Grid>
                    </Grid>
                  </SectionPanel>

                  {/* ── Overview ────────────────────────────────────────── */}
                  <SectionPanel activeSection={activeSection} sectionKey="overview">
                    <SectionTitle emoji="📋" title="Overview Details" subtitle="Key highlights shown in the overview table (e.g. Best Time, Difficulty)" color="#f59e0b" />
                    <FieldArray name="details.overview">
                      {({ push, remove }) => (
                        <Box sx={{ mt: 2 }}>
                          {values.details.overview.map((item, i) => (
                            <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 1.5, alignItems: 'center', p: 1.5, bgcolor: '#fffbeb', borderRadius: 2, border: '1px solid #fde68a' }}>
                              <Chip label={`#${i + 1}`} size="small" sx={{ bgcolor: '#f59e0b', color: '#fff', fontWeight: 700, minWidth: 36 }} />
                              <TextField label="Tag Name" name={`details.overview[${i}].tagName`} value={item.tagName} onChange={handleChange} size="small" sx={{ flex: 1 }} placeholder="e.g. Best Time" />
                              <TextField label="Tag Value" name={`details.overview[${i}].tagValue`} value={item.tagValue} onChange={handleChange} size="small" sx={{ flex: 2 }} placeholder="e.g. March – June" />
                              <IconButton onClick={() => remove(i)} color="error" size="small"><Delete fontSize="small" /></IconButton>
                            </Box>
                          ))}
                          <Button startIcon={<Add />} size="small" onClick={() => push({ tagName: '', tagValue: '' })} sx={{ borderRadius: 2, mt: 0.5 }}>Add Row</Button>
                        </Box>
                      )}
                    </FieldArray>
                  </SectionPanel>

                  {/* ── Short Itinerary ──────────────────────────────────── */}
                  <SectionPanel activeSection={activeSection} sectionKey="shortItinerary">
                    <SectionTitle emoji="🗓️" title="Short Itinerary" subtitle="Day labels used in the full itinerary dropdown" color="#0ea5e9" />
                    {!values.duration ? (
                      <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2 }}>
                        Set the <strong>Duration (days)</strong> in Basic Info first — day rows will auto-generate here.
                      </Alert>
                    ) : (
                      <Box sx={{ mt: 1.5 }}>
                        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                          Day labels are auto-generated from duration. Just fill in the <strong>title</strong> for each day.
                        </Alert>
                        {values.details.shortItinerary.map((item, i) => (
                          <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 1.5, alignItems: 'center', p: 1.5, bgcolor: '#e0f2fe', borderRadius: 2, border: '1px solid #bae6fd' }}>
                            <Chip
                              label={`Day ${i + 1}`} size="small"
                              sx={{ bgcolor: '#0284c7', color: '#fff', fontWeight: 700, minWidth: 56, flexShrink: 0 }}
                            />
                            <TextField
                              label="Day Title"
                              name={`details.shortItinerary[${i}].tagValue`}
                              value={item.tagValue}
                              onChange={(e) => {
                                setFieldValue(`details.shortItinerary[${i}].tagName`, `Day ${i + 1}`);
                                setFieldValue(`details.shortItinerary[${i}].tagValue`, e.target.value);
                              }}
                              size="small" fullWidth
                              placeholder={`e.g. Arrival at Gangtok`}
                            />
                          </Box>
                        ))}
                      </Box>
                    )}
                  </SectionPanel>

                  {/* ── Full Itinerary ───────────────────────────────────── */}
                  <SectionPanel activeSection={activeSection} sectionKey="itinerary">
                    <SectionTitle emoji="🗺️" title="Full Day-wise Itinerary" subtitle="Detailed itinerary for each day of the trip" color="#06b6d4" />
                    <FieldArray name="details.itinerary">
                      {({ push, remove }) => (
                        <Box sx={{ mt: 2 }}>
                          {values.details.itinerary.map((item, i) => (
                            <Accordion key={i} defaultExpanded={i === 0}
                              sx={{ mb: 2, borderRadius: '12px !important', border: '1.5px solid #a5f3fc', overflow: 'hidden', '&:before': { display: 'none' }, boxShadow: '0 2px 8px rgba(6,182,212,0.1)' }}>
                              <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: '#ecfeff', borderBottom: '1px solid #a5f3fc' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                                  <Avatar sx={{ width: 30, height: 30, bgcolor: '#0e7490', fontSize: 13, fontWeight: 700 }}>{i + 1}</Avatar>
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#164e63' }}>{item.headerTag || `Day ${i + 1}`}</Typography>
                                    <Typography variant="caption" sx={{ color: '#64748b' }}>{item.headerValue || 'No title set'}{item.Distance ? ` · ${item.Distance}` : ''}</Typography>
                                  </Box>
                                </Box>
                                <Button onClick={(e) => { e.stopPropagation(); remove(i); }} color="error" size="small" startIcon={<Delete />} sx={{ mr: 1 }}>Remove</Button>
                              </AccordionSummary>
                              <AccordionDetails sx={{ pt: 2 }}>
                                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f0f9ff', borderRadius: 2, border: '1px solid #bae6fd' }}>
                                  <TextField
                                    label="Select Day (from Short Itinerary)"
                                    size="small" fullWidth select value={item.headerTag || `Day ${i + 1}`}
                                    onChange={(e) => {
                                      const tag = e.target.value;
                                      const match = values.details.shortItinerary.find((si, idx) => (si.tagName || `Day ${idx + 1}`) === tag);
                                      setFieldValue(`details.itinerary[${i}].headerTag`, tag);
                                      setFieldValue(`details.itinerary[${i}].headerValue`, match ? match.tagValue : '');
                                    }}
                                    helperText={item.headerValue ? `↳ ${item.headerValue}` : 'Choose a day — the title fills automatically'}
                                    FormHelperTextProps={{ sx: { color: '#0284c7', fontWeight: 600 } }}
                                  >
                                    {values.details.shortItinerary.map((opt, idx) => {
                                      const dayLabel = opt.tagName || `Day ${idx + 1}`;
                                      return (
                                        <MenuItem key={idx} value={dayLabel}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Chip label={dayLabel} size="small" sx={{ bgcolor: '#0284c7', color: '#fff', fontWeight: 700, fontSize: 11 }} />
                                            <Typography variant="body2" sx={{ color: '#475569' }}>{opt.tagValue}</Typography>
                                          </Box>
                                        </MenuItem>
                                      );
                                    })}
                                  </TextField>
                                </Box>
                                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                                  {[
                                    { label: 'Distance', field: 'Distance', placeholder: '85 km' },
                                    { label: 'Duration', field: 'Duration', placeholder: '3 hrs' },
                                    { label: 'Altitude', field: 'Altitude', placeholder: '5800 ft' },
                                    { label: 'Time', field: 'Time', placeholder: '9:00 AM' },
                                  ].map(f => (
                                    <Grid item xs={6} md={3} key={f.field}>
                                      <TextField label={f.label} size="small" fullWidth
                                        name={`details.itinerary[${i}].${f.field}`} value={item[f.field]} onChange={handleChange} placeholder={f.placeholder} />
                                    </Grid>
                                  ))}
                                </Grid>
                                <Divider sx={{ mb: 2 }} />
                                <FieldLabel label="📝 Paragraphs" />
                                <StringArrayEditor name={`details.itinerary[${i}].para`} values={item.para || []} handleChange={handleChange} setFieldValue={setFieldValue} label="Paragraph" multiline />
                                <Divider sx={{ my: 2 }} />
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={4}>
                                    <FieldLabel label="🖼 Image URLs" />
                                    <StringArrayEditor name={`details.itinerary[${i}].images`} values={item.images || []} handleChange={handleChange} setFieldValue={setFieldValue} label="Image URL" />
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <FieldLabel label="⚡ Activities" />
                                    <StringArrayEditor name={`details.itinerary[${i}].activity`} values={item.activity || []} handleChange={handleChange} setFieldValue={setFieldValue} label="Activity" />
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <FieldLabel label="✅ Inclusions" />
                                    <StringArrayEditor name={`details.itinerary[${i}].inclusions`} values={item.inclusions || []} handleChange={handleChange} setFieldValue={setFieldValue} label="Inclusion" />
                                  </Grid>
                                </Grid>
                              </AccordionDetails>
                            </Accordion>
                          ))}
                          <Button startIcon={<Add />} variant="outlined" onClick={() =>
                            push({ headerTag: `Day ${values.details.itinerary.length + 1}`, headerValue: '', Distance: '', Duration: '', Altitude: '', Time: '', para: [''], images: [''], activity: [''], inclusions: [''] })}
                            sx={{ borderRadius: 2, borderStyle: 'dashed' }}>
                            Add Day
                          </Button>
                        </Box>
                      )}
                    </FieldArray>
                  </SectionPanel>

                  {/* ── How To Reach ─────────────────────────────────────── */}
                  <SectionPanel activeSection={activeSection} sectionKey="reach">
                    <SectionTitle emoji="🚗" title="How To Reach" subtitle="Travel instructions and route options to the destination" color="#d946ef" />
                    <Box sx={{ mt: 2 }}>
                      <TextField label="TLDR" name="details.howToReach.tldr" value={values.details.howToReach.tldr || ''} onChange={handleChange} fullWidth size="small" sx={{ mb: 2 }} />
                      <FieldLabel label="Notes" />
                      <StringArrayEditor name="details.howToReach.note" values={values.details.howToReach.note || []} handleChange={handleChange} setFieldValue={setFieldValue} label="Note" />
                      <Divider sx={{ my: 2.5 }} />
                      <FieldLabel label="📄 Description Paragraphs" />
                      <StringArrayEditor name="details.howToReach.para" values={values.details.howToReach.para} handleChange={handleChange} setFieldValue={setFieldValue} label="Paragraph" multiline />
                      <Divider sx={{ my: 2.5 }} />
                      <FieldLabel label="🗺 Route Options" />
                      <FieldArray name="details.howToReach.itineraryReach">
                        {({ push, remove }) => (
                          <Box>
                            {values.details.howToReach.itineraryReach.map((reach, ri) => (
                              <Paper key={ri} elevation={0} sx={{ p: 2, mb: 2, border: '1.5px solid #f3e8ff', borderRadius: 2, bgcolor: '#fdf4ff' }}>
                                <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center' }}>
                                  <Chip label={`Route ${ri + 1}`} size="small" sx={{ bgcolor: '#c026d3', color: '#fff', fontWeight: 700 }} />
                                  <TextField label="Route Name" size="small" sx={{ flex: 1 }} value={reach.tagName} placeholder="e.g. By Air / By Road"
                                    onChange={e => setFieldValue(`details.howToReach.itineraryReach[${ri}].tagName`, e.target.value)} />
                                  <IconButton onClick={() => remove(ri)} color="error" size="small"><Delete fontSize="small" /></IconButton>
                                </Box>
                                <StringArrayEditor name={`details.howToReach.itineraryReach[${ri}].tagValue`} values={reach.tagValue || []} handleChange={handleChange} setFieldValue={setFieldValue} label="Step" />
                              </Paper>
                            ))}
                            <Button startIcon={<Add />} size="small" onClick={() => push({ tagName: '', tagValue: [''] })} sx={{ borderRadius: 2 }}>Add Route</Button>
                          </Box>
                        )}
                      </FieldArray>
                    </Box>
                  </SectionPanel>

                  {/* ── Pricing ──────────────────────────────────────────── */}
                  <SectionPanel activeSection={activeSection} sectionKey="cost">
                    <SectionTitle emoji="💰" title="Pricing & Costing" subtitle="All pricing models for this package" color="#10b981" />

                    {/* Base single cost — always visible */}
                    <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: '#d1fae5', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#065f46', minWidth: 130 }}>Base Single Cost</Typography>
                      <TextField label="Amount (₹)" name="details.cost.singleCost" value={values.details.cost.singleCost} onChange={handleChange}
                        size="small" type="number" sx={{ maxWidth: 180 }} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
                    </Box>

                    {/* Horizontal tabs for the rest */}
                    <Box sx={{ bgcolor: '#fff', borderRadius: 2, border: '1.5px solid #a7f3d0', overflow: 'hidden' }}>
                      <Tabs
                        value={costTab} onChange={(_, v) => setCostTab(v)}
                        variant="scrollable" scrollButtons="auto"
                        sx={{
                          bgcolor: '#f0fdf4', borderBottom: '1px solid #a7f3d0',
                          '& .MuiTab-root': { fontWeight: 600, fontSize: 12, minHeight: 44 },
                          '& .Mui-selected': { color: '#059669' },
                          '& .MuiTabs-indicator': { bgcolor: '#059669' },
                        }}
                      >
                        {[
                          { label: '👥 Per Pax', count: values.details.cost.multipleCost.length },
                          { label: '📅 Days-Based', count: values.details.cost.daysCost.length },
                          { label: '💎 Value-Based', count: values.details.cost.valueCost.length },
                          { label: '✅ Inclusions', count: values.details.cost.inclusions.filter(Boolean).length },
                          { label: '❌ Exclusions', count: values.details.cost.exclusions.filter(Boolean).length },
                        ].map(({ label, count }, i) => (
                          <Tab key={i} label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {label}
                              {count > 0 && (
                                <Chip label={count} size="small"
                                  sx={{ height: 16, fontSize: 9, bgcolor: '#059669', color: '#fff', '& .MuiChip-label': { px: 0.6 } }} />
                              )}
                            </Box>
                          } />
                        ))}
                      </Tabs>

                      <Box sx={{ p: 2 }}>
                        {/* Tab 0: Per Pax */}
                        {costTab === 0 && (
                          <FieldArray name="details.cost.multipleCost">
                            {({ push, remove }) => (
                              <Box>
                                {values.details.cost.multipleCost.map((item, i) => (
                                  <Paper key={i} elevation={0} sx={{ p: 2, mb: 1.5, border: '1.5px solid #a7f3d0', borderRadius: 2 }}>
                                    <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, alignItems: 'center' }}>
                                      <Chip label={`Group ${i + 1}`} size="small" sx={{ bgcolor: '#059669', color: '#fff', fontWeight: 700 }} />
                                      <TextField label="No. of Pax" name={`details.cost.multipleCost[${i}].pax`} value={item.pax} onChange={handleChange} size="small" type="number" sx={{ maxWidth: 130 }} />
                                      <Box sx={{ flex: 1 }} />
                                      <IconButton onClick={() => remove(i)} color="error" size="small"><Delete fontSize="small" /></IconButton>
                                    </Box>
                                    <FieldArray name={`details.cost.multipleCost[${i}].pricing`}>
                                      {({ push: pp, remove: pr }) => (
                                        <Box>
                                          {item.pricing?.map((p, pi) => (
                                            <Box key={pi} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                              <TextField label="Category" name={`details.cost.multipleCost[${i}].pricing[${pi}].catagory`} value={p.catagory || p.category || ''} onChange={handleChange} size="small" sx={{ flex: 1 }} placeholder="Standard / Deluxe" />
                                              <TextField label="Price (₹)" name={`details.cost.multipleCost[${i}].pricing[${pi}].price`} value={p.price} onChange={handleChange} size="small" type="number" sx={{ flex: 1 }} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
                                              <IconButton onClick={() => pr(pi)} color="error" size="small"><Delete fontSize="small" /></IconButton>
                                            </Box>
                                          ))}
                                          <Button startIcon={<Add />} size="small" onClick={() => pp({ catagory: '', price: '' })}>Add Category</Button>
                                        </Box>
                                      )}
                                    </FieldArray>
                                  </Paper>
                                ))}
                                <Button startIcon={<Add />} size="small" onClick={() => push({ pax: '', pricing: [{ catagory: '', price: '' }] })}>Add Pax Group</Button>
                              </Box>
                            )}
                          </FieldArray>
                        )}

                        {/* Tab 1: Days-Based */}
                        {costTab === 1 && (
                          <FieldArray name="details.cost.daysCost">
                            {({ push, remove }) => (
                              <Box>
                                {values.details.cost.daysCost.map((item, i) => (
                                  <Paper key={i} elevation={0} sx={{ p: 2, mb: 1.5, border: '1.5px solid #a7f3d0', borderRadius: 2 }}>
                                    <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, alignItems: 'center' }}>
                                      <Chip label={`Slot ${i + 1}`} size="small" sx={{ bgcolor: '#059669', color: '#fff', fontWeight: 700 }} />
                                      <TextField label="Days" name={`details.cost.daysCost[${i}].days`} value={item.days} onChange={handleChange} size="small" type="number" sx={{ maxWidth: 100 }} />
                                      <Box sx={{ flex: 1 }} />
                                      <IconButton onClick={() => remove(i)} color="error" size="small"><Delete fontSize="small" /></IconButton>
                                    </Box>
                                    <FieldArray name={`details.cost.daysCost[${i}].pricing`}>
                                      {({ push: pp, remove: pr }) => (
                                        <Box>
                                          {item.pricing?.map((p, pi) => (
                                            <Box key={pi} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                              <TextField label="Category" name={`details.cost.daysCost[${i}].pricing[${pi}].catagory`} value={p.catagory || p.category || ''} onChange={handleChange} size="small" sx={{ flex: 1 }} />
                                              <TextField label="Price (₹)" name={`details.cost.daysCost[${i}].pricing[${pi}].price`} value={p.price} onChange={handleChange} size="small" type="number" sx={{ flex: 1 }} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
                                              <IconButton onClick={() => pr(pi)} color="error" size="small"><Delete fontSize="small" /></IconButton>
                                            </Box>
                                          ))}
                                          <Button startIcon={<Add />} size="small" onClick={() => pp({ catagory: '', price: '' })}>Add Category</Button>
                                        </Box>
                                      )}
                                    </FieldArray>
                                  </Paper>
                                ))}
                                <Button startIcon={<Add />} size="small" onClick={() => push({ days: '', pricing: [{ catagory: '', price: '' }] })}>Add Days Slot</Button>
                              </Box>
                            )}
                          </FieldArray>
                        )}

                        {/* Tab 2: Value-Based */}
                        {costTab === 2 && (
                          <FieldArray name="details.cost.valueCost">
                            {({ push, remove }) => (
                              <Box>
                                {values.details.cost.valueCost.map((item, i) => (
                                  <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 1, alignItems: 'center' }}>
                                    <TextField label="Type" name={`details.cost.valueCost[${i}].type`} value={item.type} onChange={handleChange} size="small" sx={{ flex: 1 }} placeholder="Premium / Budget" />
                                    <TextField label="Price (₹)" name={`details.cost.valueCost[${i}].price`} value={item.price} onChange={handleChange} size="small" type="number" sx={{ flex: 1 }} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
                                    <IconButton onClick={() => remove(i)} color="error" size="small"><Delete fontSize="small" /></IconButton>
                                  </Box>
                                ))}
                                <Button startIcon={<Add />} size="small" onClick={() => push({ type: '', price: '' })}>Add Value Type</Button>
                              </Box>
                            )}
                          </FieldArray>
                        )}

                        {/* Tab 3: Inclusions */}
                        {costTab === 3 && (
                          <StringArrayEditor name="details.cost.inclusions" values={values.details.cost.inclusions} handleChange={handleChange} setFieldValue={setFieldValue} label="Inclusion" />
                        )}

                        {/* Tab 4: Exclusions */}
                        {costTab === 4 && (
                          <StringArrayEditor name="details.cost.exclusions" values={values.details.cost.exclusions} handleChange={handleChange} setFieldValue={setFieldValue} label="Exclusion" />
                        )}
                      </Box>
                    </Box>
                  </SectionPanel>

                  {/* ── Things To Carry ──────────────────────────────────── */}
                  <SectionPanel activeSection={activeSection} sectionKey="carry">
                    <SectionTitle emoji="🎒" title="Things To Carry" subtitle="Checklist items grouped by category" color="#f43f5e" />
                    <Box sx={{ mt: 2, bgcolor: '#fff', borderRadius: 2, border: '1.5px solid #fecdd3', overflow: 'hidden' }}>
                      <Tabs
                        value={carryTab} onChange={(_, v) => setCarryTab(v)}
                        variant="scrollable" scrollButtons="auto"
                        sx={{ bgcolor: '#fff1f2', borderBottom: '1px solid #fecdd3', '& .MuiTab-root': { fontWeight: 600, fontSize: 12, minHeight: 44 } }}
                      >
                        {CARRY_TABS.map((tab, i) => (
                          <Tab key={tab} label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {tab}
                              {values.details.thingsToCarry[tab]?.length > 0 && (
                                <Chip label={values.details.thingsToCarry[tab].length} size="small"
                                  sx={{ height: 16, fontSize: 9, bgcolor: '#f43f5e', color: '#fff', '& .MuiChip-label': { px: 0.6 } }} />
                              )}
                            </Box>
                          } />
                        ))}
                      </Tabs>
                      <Box sx={{ p: 2 }}>
                        {CARRY_TABS.map((tab, i) =>
                          carryTab === i ? (
                            <ChipListEditor key={tab}
                              name={`details.thingsToCarry.${tab}`}
                              values={values.details.thingsToCarry[tab] || []}
                              setFieldValue={setFieldValue}
                              label={tab.slice(0, -1)}
                              placeholder={`Add ${tab.toLowerCase()} item and press Enter…`}
                            />
                          ) : null
                        )}
                      </Box>
                    </Box>
                  </SectionPanel>

                  {/* ── Marketing Info ────────────────────────────────────── */}
                  <SectionPanel activeSection={activeSection} sectionKey="marketing">
                    <SectionTitle emoji="📢" title="Marketing Info" subtitle="Highlights, best suited for, inclusions & exclusions for marketing" color="#fbbf24" />
                    <Box sx={{ mt: 2 }}>
                      <MarketingFieldEditor name="packageAtAGlance" values={values.packageAtAGlance} handleChange={handleChange} setFieldValue={setFieldValue} label="Package at a Glance" emoji="👀" />
                      <MarketingFieldEditor name="quickFactCheck" values={values.quickFactCheck} handleChange={handleChange} setFieldValue={setFieldValue} label="Quick Fact Check" emoji="⚡" />
                      <MarketingFieldEditor name="bestSuitedFor" values={values.bestSuitedFor} handleChange={handleChange} setFieldValue={setFieldValue} label="Best Suited For" emoji="🎯" />
                      <MarketingFieldEditor name="bestTimeToVisit" values={values.bestTimeToVisit} handleChange={handleChange} setFieldValue={setFieldValue} label="Best Time to Visit" emoji="⛅" />
                      <MarketingFieldEditor name="packageIncludes" values={values.packageIncludes} handleChange={handleChange} setFieldValue={setFieldValue} label="Package Includes" emoji="✅" />
                      <MarketingFieldEditor name="packageExcludes" values={values.packageExcludes} handleChange={handleChange} setFieldValue={setFieldValue} label="Package Excludes" emoji="❌" />
                      <MarketingFieldEditor name="whyTravelWithUs" values={values.whyTravelWithUs} handleChange={handleChange} setFieldValue={setFieldValue} label="Why Travel With Us" emoji="🤝" />
                    </Box>
                  </SectionPanel>

                  {/* ── FAQs ──────────────────────────────────────────────── */}
                  <SectionPanel activeSection={activeSection} sectionKey="faqs">
                    <SectionTitle emoji="❓" title="Frequently Asked Questions" subtitle="Common questions and answers for this package" color="#f59e0b" />
                    <FieldArray name="faqs">
                      {({ push, remove }) => (
                        <Box sx={{ mt: 2 }}>
                          {values.faqs?.map((item, i) => (
                            <Box key={i} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2, p: 2, bgcolor: '#fffbeb', borderRadius: 2, border: '1px solid #fcd34d' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Chip label={`FAQ ${i + 1}`} size="small" sx={{ bgcolor: '#f59e0b', color: '#fff', fontWeight: 700 }} />
                                <IconButton onClick={() => remove(i)} color="error" size="small"><Delete fontSize="small" /></IconButton>
                              </Box>
                              <TextField label="Question" name={`faqs[${i}].question`} value={item.question} onChange={handleChange} fullWidth size="small" placeholder="e.g. Is this package suitable for kids?" />
                              <TextField label="Answer" name={`faqs[${i}].answer`} value={item.answer} onChange={handleChange} fullWidth size="small" multiline minRows={2} placeholder="e.g. Yes, it's very suitable..." />
                            </Box>
                          ))}
                          <Button startIcon={<Add />} size="small" onClick={() => push({ question: '', answer: '' })} sx={{ borderRadius: 2, mt: 0.5, color: '#d97706', borderColor: '#fcd34d' }} variant="outlined">Add FAQ</Button>
                        </Box>
                      )}
                    </FieldArray>
                  </SectionPanel>

                  {/* ── Bottom save button ───────────────────────────────── */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2 }}>
                    <Button
                      variant="contained" type="submit" form="pkg-form" size="large"
                      sx={{
                        borderRadius: 3, px: 5, fontWeight: 700,
                        background: 'linear-gradient(135deg, #667eea, #764ba2)',
                        boxShadow: '0 8px 25px rgba(102,126,234,0.4)',
                      }}
                    >
                      {isEdit ? '💾 Update Package' : '🚀 Create Package'}
                    </Button>
                  </Box>

                </Form>
              </Box>
            </Box>
          </Box>
        )}
      </Formik>
      <SnackbarComponent />
    </>
  );
};

// ─── Mini helpers ─────────────────────────────────────────────────────────────
const SectionTitle = ({ emoji, title, subtitle, color }) => (
  <Box sx={{ pb: 2, borderBottom: `2px solid ${color}22` }}>
    <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1 }}>
      <span>{emoji}</span> {title}
    </Typography>
    {subtitle && <Typography variant="caption" sx={{ color: '#94a3b8' }}>{subtitle}</Typography>}
  </Box>
);

export default PackageForm;
